import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { Header } from "@/components/layout/Header";
import { RelatedReads } from "@/components/seo/RelatedReads";
import { getPostsBySlugs } from "@/content/blogPosts";
import { Footer } from "@/components/layout/Footer";
import { SEOContentBlock } from "@/components/seo/SEOContentBlock";
import { Button } from "@/components/ui/button";
import { Tent, Flame, Waves, Car, MapPin, Users } from "lucide-react";
import heroImage from "@/assets/glamping-hero.jpg";
import campfire from "@/assets/gallery/campfire-smores.jpg";
import riverHangout from "@/assets/gallery/river-hangout.jpg";

const TentCampingNewBraunfels = () => {
  return (
    <div className="min-h-screen">
      <SEOHead
        title="Tent Camping on River Road, New Braunfels | Son's"
        description="Tent camping on River Road, New Braunfels — riverside Guadalupe campsites with fire pits, restrooms & direct river access for tubing."
        canonical="/tent-camping-new-braunfels"
      />
      <Header />
      <main className="pt-24">
        {/* Hero */}
        <section className="relative h-[55vh] min-h-[420px]">
          <img fetchPriority="high" loading="eager" decoding="async" src={heroImage} alt="Tent camping on River Road near New Braunfels on the Guadalupe River" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/40 to-black/20" />
          <div className="absolute inset-0 flex items-center">
            <div className="container-narrow mx-auto px-4 md:px-8">
              <div className="max-w-3xl">
                <h1 className="font-heading text-4xl md:text-6xl font-bold text-white leading-tight mb-4">
                  Tent Camping Near New Braunfels on River Road
                </h1>
                <p className="text-white/90 text-lg md:text-xl max-w-2xl mb-6">
                  Riverside tent camping on the Guadalupe River — fire pits, river access, and the most iconic stretch of River Road in the Texas Hill Country.
                </p>
                <Link to="/book-now">
                  <Button size="lg" variant="default" className="text-lg px-8 py-6">Book Your Campsite</Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Intro */}
        <section className="section-padding bg-background">
          <div className="container-narrow mx-auto max-w-3xl px-4 md:px-8">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">Camping on River Road — A Texas Tradition</h2>
            <p className="text-muted-foreground text-lg leading-relaxed mb-4">
              Camping on River Road is one of the oldest and most-loved Texas summer traditions. Son's Guadalupe puts you right on it — with riverside tent camping near New Braunfels that gives you direct access to the Guadalupe River, fire pits, clean facilities, and a calm, family-friendly atmosphere. No fighting for parking, no shuttle headaches, just a pure river weekend.
            </p>
            <p className="text-muted-foreground text-lg leading-relaxed">
              Whether you're planning a quick overnight or a multi-night family getaway near New Braunfels, our campsites are the easiest way to wake up next to the water and step straight onto your tube.
            </p>
          </div>
        </section>

        {/* Features */}
        <section className="section-padding bg-muted/30">
          <div className="container-narrow mx-auto px-4 md:px-8">
            <h2 className="font-heading text-3xl font-bold text-foreground text-center mb-12">What You Get With Riverside Tent Camping</h2>
            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              {[
                { icon: Waves, label: "Direct Guadalupe River access" },
                { icon: Flame, label: "Fire pits & picnic areas" },
                { icon: Tent, label: "Spacious riverside campsites" },
                { icon: Car, label: "On-site parking" },
                { icon: MapPin, label: "Iconic River Road location" },
                { icon: Users, label: "Family & group friendly" },
              ].map((f, i) => (
                <div key={i} className="bg-card rounded-2xl p-6 shadow-soft text-center">
                  <f.icon className="w-10 h-10 text-primary mx-auto mb-3" />
                  <p className="text-foreground font-medium">{f.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Photo + content split */}
        <section className="section-padding bg-background">
          <div className="container-narrow mx-auto px-4 md:px-8 grid md:grid-cols-2 gap-10 items-center">
            <img src={campfire} alt="Campfire and s'mores at a riverside campsite near New Braunfels" className="w-full rounded-2xl shadow-elevated" loading="lazy" />
            <div>
              <h2 className="font-heading text-3xl font-bold text-foreground mb-4">Why Tent Camping on River Road Beats Everywhere Else</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                River Road follows the Guadalupe through some of the most beautiful Hill Country in Texas — towering cypress trees, limestone cliffs, and clear, spring-fed water. Tent camping near New Braunfels at Son's Guadalupe means you get that view as your front porch.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Pair your stay with cabana rentals, tubing the Guadalupe River, or a kayak rental and you have the makings of an unforgettable family getaway.
              </p>
            </div>
          </div>
        </section>

        <section className="section-padding bg-muted/30">
          <div className="container-narrow mx-auto px-4 md:px-8 grid md:grid-cols-2 gap-10 items-center">
            <div className="order-2 md:order-1">
              <h2 className="font-heading text-3xl font-bold text-foreground mb-4">Pair Camping With Tubing the Guadalupe River</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                The best way to wake up at a riverside campsite? Step out of your tent and float. Add tube rentals to any tent camping near New Braunfels booking and float right from your campsite. Bring the family, bring the friends — we'll handle the logistics.
              </p>
              <Link to="/tubing"><Button variant="outline" size="lg">Add Tubing</Button></Link>
            </div>
            <img src={riverHangout} alt="Friends hanging out by the Guadalupe River at a campsite" className="order-1 md:order-2 w-full rounded-2xl shadow-elevated" loading="lazy" />
          </div>
        </section>

        {/* CTA */}
        <section className="section-padding bg-background">
          <div className="container-narrow mx-auto text-center px-4 md:px-8">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">Book Tent Camping Near New Braunfels Today</h2>
            <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
              Spots fill up fast on summer weekends. Lock in your riverside campsite on River Road and start planning your family getaway near New Braunfels.
            </p>
            <Link to="/book-now"><Button size="lg" variant="default" className="text-lg px-10 py-6">Book Your Campsite</Button></Link>
          </div>
        </section>
      </main>
      <SEOContentBlock
        eyebrow="More on the Guadalupe"
        heading="Cabanas, Glamping & Tubing — Build Your Perfect Weekend"
        paragraphs={[
          "Pair tent camping on River Road with cabana rentals on the Guadalupe River for the daytime, glamping near New Braunfels for added comfort, or a full day of tubing the Guadalupe River. It's the easiest way to design family getaways near New Braunfels around your group.",
          "Son's Guadalupe operates multiple riverside properties near New Braunfels — pick the one that fits your style and let the river do the rest.",
        ]}
        links={[
          { label: "Cabanas", to: "/cabanas" },
          { label: "Glamping", to: "/glamping" },
          { label: "Tubing", to: "/tubing" },
          { label: "Property Map", to: "/property-map" },
        ]}
      />
      <RelatedReads posts={getPostsBySlugs(["river-road-new-braunfels-guide", "memorial-day-weekend-guadalupe-river", "fun-things-to-do-with-kids-near-new-braunfels"])} heading="From our blog" />
      <Footer />
    </div>
  );
};

export default TentCampingNewBraunfels;
